import { ReservationsEndpoint } from '@constants/ApiEndpoints';
import { AxiosInstance } from 'axios';
import { SearchResaBillResponseModel } from 'components/BillsManager/models/SearchResaBillResponseModel';
import { AddPaymentsToBillFormModel } from 'components/DialogAddPaymentsToBill/models/AddPaymentsToBillFormModel';
import {
  StayCardbillGroupUpdateModel,
  StayCardBillPaymentEdit,
  StayCardBillPaymentItemsModel,
  StayCardBillTransactionItemsModel,
  StayCardBillTransfer,
  StayCardChargesModel,
  StayCardModel,
} from 'models/Reservations/StayCard/ReservationStayCardModel';
import ReservationPaymentMethodCreateModel from 'pages/ReservationModule/SharedModels/ReservationPaymentMethodCreateModel';
import { sprintf } from 'sprintf-js';
import { InvoiceFormModel } from '../../../components/InvoiceManager/models';
import { InvoiceDataModel } from '../../../models/Reservations/StayCard/InvoiceDataModel';
import { ProformaInvoiceListItemModel } from '../../../models/Reservations/StayCard/ProformaInvoiceListModel';

interface AddPaymentModel extends ReservationPaymentMethodCreateModel {
  isCardGuest?: boolean;
}

export type AddPaymentFormModel = {
  data: AddPaymentModel[];
};

export default AddPaymentModel;

export type BasicUpdateParams = {
  identifier: number | string;
  billId: number;
};

export type UpdateParams = BasicUpdateParams & {
  // paymentId: number | string;
  transactionId: number;
  data: StayCardBillPaymentEdit;
};

export type ChargeTransferParams = BasicUpdateParams & {
  data: StayCardBillTransfer;
};

type PaymentTransferParams = BasicUpdateParams & {
  data: StayCardBillTransfer;
};

export const getAll =
  (axios: AxiosInstance) =>
  async (guestId: number, identifier: number | string): Promise<StayCardChargesModel> => {
    return await axios
      .get(sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_CHARGES_GET, guestId), {
        params: { identifier },
      })
      .then(({ data }) => data);
  };

export const getBillTransactions =
  (axios: AxiosInstance) =>
  async (
    identifier: number | string,
    billId: number,
    pagination?: { offset: number; limit: number },
    charge_group_ids?: number[]
  ): Promise<StayCardBillTransactionItemsModel> => {
    return await axios
      .get(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_CHARGES_GET_BILL_TRANSACTIONS,
          identifier,
          billId
        ),
        {
          params: {
            ...pagination,
            ...(charge_group_ids?.length ? { filters: charge_group_ids } : {}),
          },
        }
      )
      .then(({ data }) => data);
  };

export const getBillPayments =
  (axios: AxiosInstance) =>
  async (
    identifier: number | string,
    billId: number,
    pagination?: { offset: number; limit: number }
  ): Promise<StayCardBillPaymentItemsModel> => {
    return await axios
      .get(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_CHARGES_GET_BILL_PAYMENTS,
          identifier,
          billId
        ),
        { params: { ...pagination } }
      )
      .then(({ data }) => data);
  };

export const getIndivdualInvoice =
  (axios: AxiosInstance) =>
  async (guestId: string, billId: number | string): Promise<InvoiceDataModel> => {
    return await axios
      .get(sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_INVOICE, guestId, billId))
      .then(({ data }) => data);
  };

export const getProformaInvoice =
  (axios: AxiosInstance) =>
  async (guestId: string, roomReservationId: number): Promise<InvoiceDataModel> => {
    return await axios
      .get(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_PROFORMA_INVOICE,
          guestId,
          roomReservationId
        )
      )
      .then(({ data }) => data);
  };

export const printIndivdualInvoice =
  (axios: AxiosInstance) =>
  async (guestId: string, billId: number | string): Promise<any> => {
    return await axios
      .get(
        `${sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_INVOICE_PRINT,
          guestId,
          billId
        )}`,
        {
          // params: { timestamp: new Date().getTime() },
          responseType: 'arraybuffer',
          headers: { Accept: 'application/pdf' },
        }
      )
      .then(response => {
        const file = new Blob([response as any], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);

        window.open(fileURL, '_blank');
      });
  };

export const saveIndivdualInvoice =
  (axios: AxiosInstance) =>
  async (
    guestId: string,
    billId: number | string,
    data: InvoiceFormModel
  ): Promise<{ invoice_id: string; invoice_number: string; new_bill_id: number }> => {
    return await axios
      .post(
        sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_INVOICE_POST, guestId, billId),
        {
          ...data,
        }
      )
      .then(({ data }) => data);
  };

export const saveProformaInvoice =
  (axios: AxiosInstance) =>
  async (
    guestId: string,
    billId: number | string,
    data: InvoiceFormModel
  ): Promise<{ invoice_id: string; invoice_number: string }> => {
    return await axios
      .post(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_PROFORMA_INVOICE_POST,
          guestId,
          billId
        ),
        {
          ...data,
        }
      )
      .then(({ data }) => data);
  };

export const getAllProformaInvoices =
  (axios: AxiosInstance) =>
  async (guestId: string, roomReservationId: number): Promise<ProformaInvoiceListItemModel[]> => {
    return await axios
      .get(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_PROFORMA_INVOICE_GET,
          guestId,
          roomReservationId
        )
      )
      .then(({ data }) => data);
  };

/**
 * BILLING GROUP SERVICES
 * @returns Update function for create, update & delete functions
 */
export const updateBillingGroup =
  (axios: AxiosInstance) =>
  async (params: {
    billing_routes: StayCardbillGroupUpdateModel;
    identifier: number;
  }): Promise<StayCardModel> => {
    return await axios
      .put(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILLING_GROUPS_UPDATE,
          params.identifier
        ),
        { billing_routes: params.billing_routes }
      )
      .then(({ data }) => data);
  };
/******************************************************************** */

export const search =
  (axios: AxiosInstance) =>
  async (
    reservationRoomId: number,
    search: string
  ): Promise<
    // StayCardBillSearch[] |
    SearchResaBillResponseModel[]
  > => {
    return await axios
      .get(sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_CHARGES_SEARCH, reservationRoomId), {
        params: { search },
      })
      .then(({ data }) => data);
  };

export const update =
  (axios: AxiosInstance) =>
  async (params: UpdateParams): Promise<StayCardModel> => {
    return await axios
      .patch(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_UPDATE,
          params.identifier,
          params.billId,
          params.transactionId
        ),
        params.data
      )
      .then(({ data }) => data);
  };

export const transferCharge =
  (axios: AxiosInstance) =>
  async (params: ChargeTransferParams): Promise<StayCardModel> => {
    return await axios
      .patch(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_CHARGE_TRANSFERT,
          params.identifier,
          params.billId
          // params.transactionId
        ),
        params.data
      )
      .then(({ data }) => data);
  };

export const transferPayment =
  (axios: AxiosInstance) =>
  async (params: PaymentTransferParams): Promise<StayCardModel> => {
    return await axios
      .patch(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_PAYMENTS_TRANSFERT,
          params.identifier,
          params.billId
          // params.paymentId
        ),
        params.data
      )
      .then(({ data }) => data);
  };

export const addNewBill =
  (axios: AxiosInstance) =>
  async ({
    reservationRoomId,
    ...data
  }: {
    reservationRoomId: number;
    resource_id: number;
    resource_type: string;
  }) => {
    return await axios
      .post(
        sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_CREATE, reservationRoomId),
        data
      )
      .then(({ data }) => data);
  };
export interface PostTransactionModel {
  reservationRoomId: number;
  billId: number;
  arAcount?: number | undefined;
  data: {
    items: {
      id: number;
      quantity: number;
      type: 'item' | 'package';
    }[];
    charge_codes: {
      id: number;
      amount: number;
      justification: string;
    }[];
  };
}
export const postTransaction =
  (axios: AxiosInstance) =>
  async ({ reservationRoomId, billId, arAcount, data }: PostTransactionModel) => {
    return await axios
      .post(
        sprintf(
          arAcount
            ? ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_POST_TRANSACTION_AR_ACCOUNT
            : ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_POST_TRANSACTION,
          reservationRoomId,
          billId,
          arAcount
        ),
        { ...data }
      )
      .then(({ data }) => data);
  };

export const transferTransactionsToCompany =
  (axios: AxiosInstance) =>
  async ({ reservationRoomId, billId }: { reservationRoomId: number; billId: number }) => {
    return await axios
      .patch(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_TRANSFER_TRANSACTION_TO_COMPANY,
          reservationRoomId,
          billId
        )
      )
      .then(({ data }) => data);
  };

export const postPayment =
  (axios: AxiosInstance) =>
  async ({
    data,
    reservationRoomId,
    billId,
  }: {
    reservationRoomId: number;
    billId: number;
    data: AddPaymentsToBillFormModel;
  }) => {
    return await axios
      .post(
        sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_BILL_POST_PAYMENT,
          reservationRoomId,
          billId
        ),
        { ...data }
      )
      .then(({ data }) => data);
  };

export const deleteBill =
  (axios: AxiosInstance) =>
  async ({ reservationRoomId, billId }: { reservationRoomId: number; billId: number }) => {
    return await axios
      .delete(
        sprintf(ReservationsEndpoint.RESERVATION_STAY_CARD_DELETE_BILL, reservationRoomId, billId)
      )
      .then(({ data }) => data);
  };

export const downloadReceiptPayment =
  (axios: AxiosInstance) =>
  async (payload: {
    reservationRoomId: number;
    billId: number;
    payments?: Array<number>;
    allPayments?: boolean;
  }): Promise<any> => {
    return await axios
      .get(
        `${sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_DOWNLOAD_RECEIPT_PAYMENT_BILL,
          payload.reservationRoomId,
          payload.billId
        )}`,
        {
          params: {
            ...(payload?.payments ? { payments: payload.payments } : {}),
            ...(payload?.allPayments ? { all_payments: true } : {}),
          },
          responseType: 'arraybuffer',
          headers: { Accept: 'application/pdf' },
        }
      )
      .then(response => {
        const file = new Blob([response as any], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);

        // Ouvrir dans un nouvel onglet
        window.open(fileURL, '_blank');
      });
  };

export const downloadGuestFolio =
  (axios: AxiosInstance) =>
  async (payload: {
    reservationRoomId: number;
    billId: number;
    transactions?: Array<number>;
    payments?: Array<number>;
  }): Promise<any> => {
    return await axios
      .get(
        `${sprintf(
          ReservationsEndpoint.RESERVATION_STAY_CARD_GUEST_FOLIO_BILL,
          payload.reservationRoomId,
          payload.billId
        )}`,
        {
          params: {
            ...((payload.transactions || []).length > 0
              ? { transactions: payload.transactions }
              : {}),
            ...((payload.payments || []).length > 0 ? { payments: payload.payments } : {}),
          },
          responseType: 'arraybuffer',
          headers: { Accept: 'application/pdf' },
        }
      )
      .then(response => {
        const file = new Blob([response as any], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);

        // Ouvrir dans un nouvel onglet
        window.open(fileURL, '_blank');
      });
  };
